<script setup lang="ts">
import * as PDFJS from 'pdfjs-dist'
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api'
PDFJS.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${PDFJS.version}/build/pdf.worker.min.mjs`
import { onMounted, PropType, ref, toRaw, watch } from 'vue'
/**
 * define props
 */
const props = defineProps({
  currentPage: {
    type: Number,
    default: 1,
  },
  docPath: {
    type: String,
    required: true,
  },
  docPageKeyStore: {
    type: String,
    default: '',
  },
  scaleText: {
    type: String,
    default: '1',
  },
  highlightActive: {
    type: Boolean,
    default: false,
  },
  canClickPage: {
    type: Boolean,
    default: false,
  },
  scrollStyle: {
    type: String as PropType<'smooth' | 'instant'>,
    default: 'instant',
  },
  detectScroll: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['change-page', 'load-success', 'click-page'])

const activePage = ref<number>(1)
const containerRef = ref<HTMLElement>()
const parentId = ref<string>(Math.random().toString(36).substr(2, 6))
const totalPage = ref<number>(0)
const scale = ref<number>(1)

const pdf = ref<PDFDocumentProxy>()
const pdfLoadingTask = ref<PDFJS.PDFDocumentLoadingTask>()

/**
 * Returns an array of the actual page width and height based on props and
 * aspect ratio.
 * @param ratio - Page aspect ratio.
 */
const getPageDimensions = (ratio: number): [number, number] => {
  let width: number
  let height: number

  width = document.getElementById(parentId.value)?.clientWidth || 300
  height = width * ratio

  return [width, height]
}

const adjustPageSize = async () => {
  const pageNums = [...Array(totalPage.value + 1).keys()].slice(1)
  const pdfDocProxy = toRaw(pdf.value)
  if (!pdfDocProxy) return
  for (const num of pageNums) {
    const page = await pdfDocProxy.getPage(num)
    const viewWidth = page.view[2] - page.view[0]
    const viewHeight = page.view[3] - page.view[1]
    const [actualWidth] = getPageDimensions(viewHeight / viewWidth)

    const pageWidth = viewWidth
    if (props.scaleText === 'fit-width') {
      scale.value = actualWidth / pageWidth
    } else if (props.scaleText === 'fit-height') {
      const pageActualHeight = document.getElementById(`${parentId.value}`)?.clientHeight
      scale.value = pageActualHeight ? pageActualHeight / viewHeight : 1
    } else {
      scale.value = Number(props.scaleText)
    }
    const viewport = page.getViewport({
      scale: scale.value,
    })

    const canvas = document.getElementById(`c-${parentId.value}-${num}`) as HTMLCanvasElement
    if (canvas) {
      canvas.width = 1
      canvas.height = 1
      canvas.getContext('2d')?.clearRect(0, 0, 1, 1)
      canvas.height = Math.floor(viewport.height)
      canvas.width = Math.floor(viewport.width)
      canvas.style.width = Math.floor(viewport.width) + 'px'
      canvas.style.height = Math.floor(viewport.height) + 'px'
      // Render PDF page into canvas context
      page.render({
        canvas: canvas,
        canvasContext: canvas.getContext('2d') || undefined,
        viewport: viewport,
      }).promise
    }
  }
}

const scrollToPageAfterLoad = () => {
  scrollToPage(props.currentPage, 'instant')
  activePage.value = props.currentPage
}

const loadPDF = async () => {
  pdfLoadingTask.value = PDFJS.getDocument(props.docPath)
  pdfLoadingTask.value.promise
    .then(async function (pdfDoc) {
      pdf.value = pdfDoc
      totalPage.value = pdfDoc.numPages

      await adjustPageSize()
      emit('load-success', { totalPage: totalPage.value, scale: scale.value })
      scrollToPageAfterLoad()
      if (props.detectScroll) {
        containerRef?.value?.addEventListener('scroll', () => {
          const pageHeight =
            (document.getElementById(`${parentId.value}-1`)?.offsetHeight || 0) + 16

          if (containerRef?.value) {
            const scrollTop = containerRef?.value?.scrollTop
            const latestPage = Math.round(scrollTop / pageHeight) + 1
            const page = latestPage >= totalPage.value ? totalPage.value : latestPage
            activePage.value = page
            emit('change-page', page)
          }
        })
      }
    })
    .catch(function (error) {
      // Handle any errors during loading
      console.error('Error loading PDF:', error.message)
    })
}

const scrollToPage = (page: number, style?: 'smooth' | 'instant') => {
  const elementPage = document.getElementById(`${parentId.value}-${page}`)
  if (elementPage) {
    elementPage.scrollIntoView({ behavior: style || props.scrollStyle })
  }
}

const clickChangePage = (page: number) => {
  emit('click-page', page)
}

watch(
  () => props.currentPage,
  () => {
    if (activePage.value !== props.currentPage) {
      activePage.value = props.currentPage
      scrollToPage(activePage.value)
    }
  },
)
watch(
  () => props.scaleText,
  () => {
    adjustPageSize()
  },
)

onMounted(() => {
  loadPDF()
})
</script>

<template>
  <div ref="containerRef" class="pages-container">
    <div :id="parentId" class="w-full h-full">
      <div v-for="pageNum in totalPage" :key="pageNum" class="mb-4">
        <slot name="before-page" :page="pageNum" />

        <div
          :id="parentId && `${parentId}-${pageNum}`"
          :style="{
            '--scale-factor': scale,
            position: 'relative',
            opacity: highlightActive ? (pageNum === activePage ? 1 : 0.7) : 1,
            cursor: canClickPage ? 'pointer' : 'default',
          }"
          @click="clickChangePage(pageNum)"
        >
          <canvas :id="`c-${parentId}-${pageNum}`" />

          <!-- <div v-if="textLayer" class="textLayer" /> -->

          <!-- <div v-if="annotationLayer" class="annotationLayer" /> -->
        </div>

        <slot name="after-page" :page="pageNum" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pages-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
}
</style>
